import React, { useEffect, useState } from "react";
import { collection, getDocs, doc, updateDoc } from "firebase/firestore";
import { db } from "../firebase";
import { getEmployeeCredentialsPath } from "../services/firestorePaths";
import { useLocation, useNavigate } from "react-router-dom";
import { checkStatus } from "../services/credentialStatus";
import { backTarget } from "../utils/backTarget";
import {
  ArrowLeft,
  Bell,
  CheckCircle2,
  AlertTriangle,
  AlertCircle,
  CalendarDays,
  Save,
  UserCircle2,
  ShieldCheck,
  Info,
  Sparkles,
} from "lucide-react";

// The status rule is shared with the home page's snippet (moved there unchanged).

// Derive visual tone from the status string (purely UI — does not alter the status string itself)
const statusStyle = (status) => {
  if (status.startsWith("❌"))
    return {
      chip: "bg-rose-400/15 text-rose-200 border-rose-400/30",
      dot: "bg-rose-400 shadow-[0_0_10px_2px_rgba(244,63,94,0.7)]",
      Icon: AlertCircle,
      severity: 3,
    };
  if (status.includes("Expiring tomorrow"))
    return {
      chip: "bg-orange-400/15 text-orange-200 border-orange-400/30",
      dot: "bg-orange-400 shadow-[0_0_10px_2px_rgba(251,146,60,0.7)]",
      Icon: AlertTriangle,
      severity: 3,
    };
  if (status.includes("within 1 month"))
    return {
      chip: "bg-amber-400/15 text-amber-200 border-amber-400/30",
      dot: "bg-amber-400 shadow-[0_0_10px_2px_rgba(251,191,36,0.7)]",
      Icon: AlertTriangle,
      severity: 2,
    };
  if (status.includes("within 6 months"))
    return {
      chip: "bg-yellow-400/15 text-yellow-200 border-yellow-400/30",
      dot: "bg-yellow-400 shadow-[0_0_10px_2px_rgba(250,204,21,0.6)]",
      Icon: AlertTriangle,
      severity: 1,
    };
  if (status.startsWith("ℹ️"))
    return {
      chip: "bg-slate-400/15 text-slate-300 border-slate-400/30",
      dot: "bg-slate-400",
      Icon: Info,
      severity: 0,
    };
  return {
    chip: "bg-emerald-400/15 text-emerald-300 border-emerald-400/30",
    dot: "bg-emerald-400 shadow-[0_0_10px_2px_rgba(52,211,153,0.7)]",
    Icon: CheckCircle2,
    severity: 0,
  };
};

const PageBackground = () => (
  <div className="fixed inset-0 -z-10 overflow-hidden">
    <img
      src="/bg_ai.jpg"
      alt=""
      aria-hidden="true"
      className="absolute inset-0 w-full h-full object-cover"
    />
    <div className="absolute inset-0 bg-gradient-to-br from-[#070418]/90 via-[#0c0726]/85 to-[#04030f]/95" />
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(124,58,237,0.25),_transparent_55%)]" />
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,_rgba(37,99,235,0.22),_transparent_55%)]" />
    <div className="absolute -top-32 -left-32 w-[520px] h-[520px] rounded-full bg-purple-600/25 blur-[140px]" />
    <div className="absolute top-1/3 -right-40 w-[520px] h-[520px] rounded-full bg-blue-600/25 blur-[140px]" />
  </div>
);

const NotificationsPage = () => {
  const [notifications, setNotifications] = useState([]);
  const [savingId, setSavingId] = useState(null);
  const [savedId, setSavedId] = useState(null);
  const navigate = useNavigate();
  // Back: home when opened from the home page, else the Admin Dashboard.
  const back = backTarget(useLocation().state, "/dashboard", "Back");

  useEffect(() => {
    const fetchData = async () => {
      const querySnapshot = await getDocs(collection(db, getEmployeeCredentialsPath()));
      const filtered = [];

      querySnapshot.forEach((docSnap) => {
        const data = docSnap.data();
        const { NAME, CV_EXP, GCP_EXP, IATA_EXP, ML_EXP, CPR_EXP, CITI_EXP } = data;

        const credentials = [
          { field: "CV_EXP", label: "CV", value: CV_EXP, status: checkStatus(CV_EXP) },
          { field: "GCP_EXP", label: "GCP", value: GCP_EXP, status: checkStatus(GCP_EXP) },
          { field: "IATA_EXP", label: "IATA", value: IATA_EXP, status: checkStatus(IATA_EXP) },
          { field: "ML_EXP", label: "Medical License", value: ML_EXP, status: checkStatus(ML_EXP) },
          { field: "CPR_EXP", label: "CPR", value: CPR_EXP, status: checkStatus(CPR_EXP) },
          { field: "CITI_EXP", label: "CITI", value: CITI_EXP, status: checkStatus(CITI_EXP) },
        ];

        const filteredCreds = credentials.filter(
          (cred) => cred.status !== "✅ Valid" && cred.status !== "ℹ️ Not Required"
        );

        if (filteredCreds.length > 0) {
          filtered.push({
            id: docSnap.id,
            NAME,
            credentials: filteredCreds,
          });
        }
      });

      setNotifications(filtered);
    };

    fetchData();
  }, []);

  const handleChange = (employeeIndex, credIndex, newValue) => {
    const updated = [...notifications];
    updated[employeeIndex].credentials[credIndex].value = newValue;
    updated[employeeIndex].credentials[credIndex].status = checkStatus(newValue);
    setNotifications(updated);
  };

  const handleSave = async (employee) => {
    const updatedFields = {};
    employee.credentials.forEach((cred) => {
      updatedFields[cred.field] = cred.value;
    });

    setSavingId(employee.id);
    const ref = doc(db, getEmployeeCredentialsPath(), employee.id);
    await updateDoc(ref, updatedFields);
    setSavingId(null);
    setSavedId(employee.id);
    alert("✅ Changes saved!");
  };

  const totalAlerts = notifications.reduce((n, e) => n + e.credentials.length, 0);
  const criticalCount = notifications.reduce(
    (n, e) => n + e.credentials.filter((c) => c.status.startsWith("❌")).length,
    0
  );

  return (
    <div className="relative min-h-screen w-full text-slate-100 font-[Manrope]">
      <PageBackground />

      <div className="relative px-3 sm:px-4 lg:px-6 py-4 max-w-4xl mx-auto">
        {/* Top bar */}
        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="flex items-center gap-2 min-w-0">
            <button
              onClick={() => navigate(back.to)}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] hover:border-white/20 text-xs text-slate-300 hover:text-white transition"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Back
            </button>
            <div className="flex items-center gap-2 min-w-0">
              <div className="relative shrink-0">
                <div className="absolute inset-0 bg-gradient-to-br from-rose-500 to-orange-500 rounded-xl blur-md opacity-70" />
                <div className="relative w-9 h-9 rounded-xl bg-gradient-to-br from-rose-500/30 via-orange-500/30 to-amber-500/30 border border-white/15 ring-1 ring-white/20 flex items-center justify-center">
                  <Bell className="w-4 h-4 text-white" strokeWidth={2.4} />
                  {totalAlerts > 0 && (
                    <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-rose-500 shadow-[0_0_8px_2px_rgba(244,63,94,0.7)] animate-pulse" />
                  )}
                </div>
              </div>
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.25em] text-purple-300/80 font-semibold leading-tight">
                  System
                </div>
                <h1 className="text-base sm:text-lg font-semibold tracking-tight text-white leading-tight truncate">
                  Notifications
                </h1>
              </div>
            </div>
          </div>

          <div className="hidden sm:flex items-center gap-2">
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/[0.04] border border-white/10 text-[10px] uppercase tracking-wider text-slate-300">
              <span className="font-semibold text-white">{totalAlerts}</span>
              alerts
            </div>
            {criticalCount > 0 && (
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-rose-500/15 border border-rose-400/30 text-[10px] uppercase tracking-wider text-rose-200">
                <AlertCircle className="w-3 h-3" />
                <span className="font-semibold">{criticalCount}</span>
                critical
              </div>
            )}
          </div>
        </div>

        {/* Empty state */}
        {notifications.length === 0 ? (
          <div className="rounded-2xl border border-emerald-400/20 bg-white/[0.03] backdrop-blur-2xl p-10 text-center relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/10 via-transparent to-teal-500/10 pointer-events-none" />
            <div className="relative">
              <div className="mx-auto w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-500/30 to-teal-500/30 border border-emerald-300/30 flex items-center justify-center mb-3 shadow-[0_0_30px_-5px_rgba(52,211,153,0.5)]">
                <ShieldCheck className="w-6 h-6 text-emerald-200" />
              </div>
              <h3 className="text-sm font-semibold text-white">All clear</h3>
              <p className="text-xs text-slate-400 mt-1">
                All credentials are valid or not required.
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-2.5">
            {notifications.map((emp, empIndex) => {
              const isSaving = savingId === emp.id;
              const justSaved = savedId === emp.id && !isSaving;

              return (
                <div
                  key={emp.id}
                  className="relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-2xl shadow-[0_8px_30px_-12px_rgba(0,0,0,0.4)]"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-rose-500/[0.05] via-transparent to-amber-500/[0.05] pointer-events-none" />
                  <div className="absolute top-0 inset-x-0 h-[2px] bg-gradient-to-r from-rose-400 via-amber-400 to-purple-400 opacity-60" />

                  <div className="relative p-3.5">
                    {/* Employee header */}
                    <div className="flex items-center justify-between gap-3 mb-3">
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-purple-500 via-indigo-500 to-blue-500 flex items-center justify-center text-xs font-bold text-white ring-1 ring-white/20 shrink-0">
                          {(emp.NAME || "—").charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold leading-none">
                            Employee
                          </div>
                          <h3 className="text-sm font-semibold text-white truncate leading-tight">
                            {emp.NAME || "—"}
                          </h3>
                        </div>
                      </div>
                      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold border bg-rose-400/15 text-rose-200 border-rose-400/30">
                        <span className="w-1.5 h-1.5 rounded-full bg-rose-400 shadow-[0_0_8px_rgba(244,63,94,0.7)]" />
                        {emp.credentials.length} expiring
                      </span>
                    </div>

                    {/* Credentials list */}
                    <ul className="space-y-2">
                      {emp.credentials.map((cred, credIndex) => {
                        const s = statusStyle(cred.status);
                        const Icon = s.Icon;
                        return (
                          <li
                            key={cred.field}
                            className="rounded-xl border border-white/10 bg-white/[0.03] p-2.5 hover:bg-white/[0.05] transition"
                          >
                            <div className="flex items-center justify-between gap-2 mb-1.5 flex-wrap">
                              <div className="flex items-center gap-2 min-w-0">
                                <UserCircle2 className="w-3.5 h-3.5 text-purple-300 shrink-0" />
                                <span className="text-xs font-semibold text-white">
                                  {cred.label}
                                </span>
                                <span className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">
                                  Expiration
                                </span>
                              </div>
                              <span
                                className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold border backdrop-blur-md ${s.chip}`}
                              >
                                <Icon className="w-3 h-3" />
                                {cred.status}
                              </span>
                            </div>
                            <div className="relative">
                              <CalendarDays className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500 pointer-events-none" />
                              <input
                                type="text"
                                value={cred.value || ""}
                                onChange={(e) =>
                                  handleChange(empIndex, credIndex, e.target.value)
                                }
                                placeholder="DDMMMYYYY"
                                className="w-full pl-9 pr-3 py-1.5 rounded-lg bg-white/[0.04] border border-white/10 text-xs text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-purple-400/50 focus:bg-white/[0.06] focus:ring-2 focus:ring-purple-500/20 transition font-mono tracking-wider"
                              />
                            </div>
                          </li>
                        );
                      })}
                    </ul>

                    {/* Save bar */}
                    <div className="mt-3 flex items-center justify-end gap-2">
                      {justSaved && (
                        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-emerald-300 font-semibold">
                          <CheckCircle2 className="w-3 h-3" />
                          Saved
                        </span>
                      )}
                      <button
                        onClick={() => handleSave(emp)}
                        disabled={isSaving}
                        className="group relative inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl bg-gradient-to-r from-purple-500 via-indigo-500 to-blue-500 ring-1 ring-white/20 text-xs font-semibold text-white hover:shadow-[0_8px_24px_-6px_rgba(139,92,246,0.6)] active:scale-[0.98] transition-all disabled:opacity-60 disabled:cursor-not-allowed overflow-hidden"
                      >
                        <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/25 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                        <Save className="w-3.5 h-3.5 relative" />
                        <span className="relative">
                          {isSaving ? "Saving…" : "Save Changes"}
                        </span>
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Footer hint */}
        {notifications.length > 0 && (
          <p className="mt-3 text-center text-[10px] uppercase tracking-[0.2em] text-slate-500 flex items-center justify-center gap-1.5">
            <Sparkles className="w-3 h-3 text-purple-300/70" />
            Date format: DDMMMYYYY (e.g., 15JAN2026)
          </p>
        )}

        <div className="h-6" />
      </div>
    </div>
  );
};

export default NotificationsPage;
